import moment from 'moment';

import { BadgeModel, AddressModel, ProjectNewModel, WorkerModel, FcaLearningModel, CertificationModel, ResourceModel } from '../modules/models';
import { isTempId, isEmpty, parseFloatNumber, roundNumber, sanitizeNumber } from './generalUtils';
import { sanitizeAddress } from './addressUtils';
import { sanitizeCertificationGroups } from './certificationGroupsUtils';
import { sanitizeTrainingsGroups } from './trainingGroupsUtils';
import { IFcaLearningGroup } from 'modules/models/fcaLearning';
import { BillingEngineRuleCompanyType, BillingEngineRuleItemType, billingEngineRuleCompanyTypeMap } from 'modules/models/project-new';

export const sanitizeCompanyList = list =>
  list
    .filter(company => !isTempId(company.id) && company.id !== null)
    .map(company => ({
      id: company.id,
      role: company.role,
      isTaxExempt: company.isTaxExempt,
      ...(company.role === ProjectNewModel.CompanyRole.GENERAL_CONTRACTOR
        ? {
            canViewAllWorkers: company.canViewAllWorkers,
            canAssignAllWorkers: company.canAssignAllWorkers,
            canCreateOrManageAllWorkers: company.canCreateOrManageAllWorkers,
          }
        : {}),
    }));

export const sanitizeLocations = locations =>
  locations
    .filter(location => location.id !== null && location.name)
    .map(location => ({
      id: location.id,
      name: location.name,
      locationCodes: location.locationCodes,
    }));

export const sanitizeBadgeTemplate = (template: BadgeModel.IBadgeTemplate): BadgeModel.IBadgeTemplate => ({
  ...template,
  fontColor: template.layout !== BadgeModel.BadgeLayout.WHITE_HEADER ? '444444' : template.fontColor,
});

export const sanitizeProject = (project: Partial<ProjectNewModel.IProject>): Partial<ProjectNewModel.IProject> => ({
  ...project,
  ccv: project.ccv ? sanitizeNumber(project.ccv as any) : null,
  relatedCompanies: project.relatedCompanies ? sanitizeCompanyList(project.relatedCompanies) : null,
  naeId: isEmpty(project.naeId) ? null : /* istanbul ignore next */ project.naeId,
  regionId: isEmpty(project.regionId) ? null : project.regionId,
  categoryId: isEmpty(project.categoryId) ? null : project.categoryId,
  timeZoneId: isEmpty(project.timeZoneId) ? null : project.timeZoneId,
  billingModelType: project.billingModelType ? parseInt(String(project.billingModelType), 10) : ProjectNewModel.BillingModelType.BADGES,
  badgeBillingModel:
    project.badgeBillingModel && project.billingModelType === ProjectNewModel.BillingModelType.BADGES
      ? {
          ...project.badgeBillingModel,
          billingModelResponsibleCompanies: project.badgeBillingModel?.billingModelResponsibleCompanies?.filter?.(company => (
            company.billedCompanyId ? project.relatedCompanies?.find?.(relatedCompany => relatedCompany.id === company.billedCompanyId)?.id || company.isBilledPerCompany : company.isBilledPerCompany
          )),
          badgePrice: isPriceNotEmpty(project.badgeBillingModel?.badgePrice) ? parseFloatNumber(project.badgeBillingModel.badgePrice) : null,
          reprintingCost: isPriceNotEmpty(project.badgeBillingModel.reprintingCost)
            ? /* istanbul ignore next */ parseFloatNumber(project.badgeBillingModel.reprintingCost)
            : null,
          visitorBadgePrice: isPriceNotEmpty(project.badgeBillingModel.visitorBadgePrice)
            ? parseFloatNumber(project.badgeBillingModel.visitorBadgePrice)
            : null,
          visitorReprintingCost: isPriceNotEmpty(project.badgeBillingModel.visitorReprintingCost)
            ? parseFloatNumber(project.badgeBillingModel.visitorReprintingCost)
            : null,
          billedCompanyId: project.badgeBillingModel.billedCompanyId || project.badgeBillingModel?.billedCompany?.id || null,
          isChangeInBadgeBillingModel: project.badgeBillingModel?.isChangeInBadgeBillingModel || project.badgeBillingModel?.billingModelResponsibleCompanies?.filter?.(company => (
            company.billedCompanyId ? project.relatedCompanies?.find?.(relatedCompany => relatedCompany.id === company.billedCompanyId)?.id : false
          ))?.length !== project.badgeBillingModel?.billingModelResponsibleCompanies?.length,
        }
      : null,
  seatBillingModel:
    project.seatBillingModel && project.billingModelType === ProjectNewModel.BillingModelType.SEATS
      ? {
          estimatedWorkersNumber: project.seatBillingModel.estimatedWorkersNumber,
          isFixedSeatPrice: project.seatBillingModel.isFixedSeatPrice,
          seatPrice: isPriceNotEmpty(project.seatBillingModel?.seatPrice) ? parseFloatNumber(project.seatBillingModel.seatPrice) : null,
          billingTierId: project.seatBillingModel.billingTierId || project.seatBillingModel.billingTier?.id || null,
          billedCompanyId: project.seatBillingModel.billedCompanyId || project.seatBillingModel?.billedCompany?.id || null,
          reprintingCost: isPriceNotEmpty(project.seatBillingModel.reprintingCost) ? parseFloatNumber(project.seatBillingModel.reprintingCost) : null,
          visitorBadgePrice: isPriceNotEmpty(project.seatBillingModel.visitorBadgePrice) ? parseFloatNumber(project.seatBillingModel.visitorBadgePrice) : null,
          visitorReprintingCost: isPriceNotEmpty(project.seatBillingModel.visitorReprintingCost)
            ? parseFloatNumber(project.seatBillingModel.visitorReprintingCost)
            : null,
        }
      : !project.seatBillingModel && project.billingModelType === ProjectNewModel.BillingModelType.SEATS
      ? ProjectNewModel.getFallbackSeatBillingModel()
      : null,
  description: isEmpty(project.description) ? null : project.description,
  jobSiteAddress: project.jobSiteAddress ? sanitizeAddress(project.jobSiteAddress) : null,
  locations: project.locations ? sanitizeLocations(project.locations) : null,
  badgingSiteAddress: null,
  badgingSiteAddresses: (project.badgingSiteAddresses || []).length > 0 ? project.badgingSiteAddresses.map(address => sanitizeAddress(address)) : [],
  mailingAddress: project.mailingAddress ? sanitizeAddress(project.mailingAddress) : null,
  certificationGroups: sanitizeCertificationGroups(project.certificationGroups),
  trainingGroups: sanitizeTrainingsGroups(project.trainingGroups),
  consentFormFields: project.consentFormFields,
  generalContractorBadgeTemplate: sanitizeBadgeTemplate(project.generalContractorBadgeTemplate),
  subcontractorBadgeTemplate: sanitizeBadgeTemplate(project.subcontractorBadgeTemplate),
  visitorBadgeTemplate: project.visitorBadgeTemplate,
  clearLocationManagerAfterHrs:
    project?.defaultLocationManagerFlushHours && Number(project.clearLocationManagerAfterHrs) > 0 ? project.clearLocationManagerAfterHrs : null,
  projectCodes:
    project.projectCodes?.length > 0
      ? project.projectCodes
          ?.filter(pc => pc.code)
          ?.map(pc => ({
            ...pc,
            projectCodeVisibilityRules: pc.projectCodeVisibilityRules?.map(rule => ({ companyId: rule.value, companyName: rule.label })),
          }))
      : [],
  unverifiedClientCreationAllowedCompanies: project?.unverifiedClientCreationAllowedCompanies?.length
    ? project?.unverifiedClientCreationAllowedCompanies?.map(item => item.id)
    : [],
  fcaProjectDirectorId: project?.fcaProjectDirector?.id
});

export const preloadProject = (project: ProjectNewModel.IProject) => ({
  ...project,
  regionId: project.region?.id ? project.region.id : null,
  categoryId: project.category?.id ? project.category.id : null,
  naeId: project.nae?.id ? project.nae.id : null,
  typeId: project.type?.id ? project.type.id : null,
  subTypeId: project.subType?.id ? project.subType.id : null,
  timeZoneId: project.timeZone?.id ? project.timeZone.id : null,
  billingModelType: isEmpty(project.billingModelType) ? ProjectNewModel.BillingModelType.BADGES : project.billingModelType,
  jobSiteAddress: isEmpty(project.jobSiteAddress) ? AddressModel.getFallbackAddress() : project.jobSiteAddress,
  locations: isEmpty(project.locations) ? [] : project.locations,
  generalContractorBadgeTemplate: isEmpty(project.generalContractorBadgeTemplate)
    ? BadgeModel.getFallbackBadgeTemplate()
    : project.generalContractorBadgeTemplate,
  subcontractorBadgeTemplate: isEmpty(project.subcontractorBadgeTemplate) ? BadgeModel.getFallbackBadgeTemplate() : project.subcontractorBadgeTemplate,
  visitorBadgeTemplate: isEmpty(project.visitorBadgeTemplate) ? BadgeModel.getFallbackVisitorBadgeTemplate() : project.visitorBadgeTemplate,
  consentFormFields: isEmpty(project.consentFormFields) ? ProjectNewModel.getFallbackProject().consentFormFields : project.consentFormFields,
  certificationGroups: !isEmpty(project.certificationGroups) ? project.certificationGroups : [],
  trainingGroups: !isEmpty(project.trainingGroups) ? project.trainingGroups : [],
  consentFormLegals: !isEmpty(project.consentFormLegals) ? project.consentFormLegals : [],
});

export const getSeatPrice = (isFixed: boolean, seatPrice: number, tierPrice: number) => (isFixed ? seatPrice : tierPrice || 0);

export const getTotalPrice = (workers: number, seatPrice: number) => roundNumber(workers * seatPrice);

export const isBilledPerCompany = (project: ProjectNewModel.IProject) =>
  !!(project?.billingModelType === ProjectNewModel.BillingModelType.BADGES && project?.badgeBillingModel?.isBilledPerCompany);

export const getPlannedMonths = (startDate: string, endDate: string) => (endDate && startDate ? moment(endDate).diff(moment(startDate), 'months') : 0);

export const getProjectBadgeResourceRequest = (pendingBadges, fileMap, restOfFiles?: { [key: string]: string }) => {
  const initialObject = {
    generalContractorTemplate: null,
    subcontractorTemplate: null,
    visitorTemplate: null,
  };

  if (
    pendingBadges.includes(ProjectNewModel.ProjectBadgeLogos.GENERAL_CONTRACTOR_BADGE_LOGO) ||
    pendingBadges.includes(ProjectNewModel.ProjectBadgeTemplates.GENERAL_CONTRACTOR_BADGE_TEMPLATE_FILE)
  ) {
    initialObject.generalContractorTemplate = {
      logoFileName: fileMap.generalContractorBadgeLogo ? (Object.values(fileMap.generalContractorBadgeLogo)[0] as any)?.file?.name : '',
      templateFileName: fileMap.generalContractorBadgeTemplate ? (Object.values(fileMap.generalContractorBadgeTemplate)[0] as any)?.file?.name : '',
    };
  } else if (restOfFiles.generalContractorBadgeLogo) {
    initialObject.generalContractorTemplate = {
      logoFileName: restOfFiles.generalContractorBadgeLogo,
    };
  } else if (restOfFiles.generalContractorBadgeTemplate) {
    initialObject.generalContractorTemplate = {
      templateFileName: restOfFiles.generalContractorBadgeTemplate,
    };
  }

  if (
    pendingBadges.includes(ProjectNewModel.ProjectBadgeLogos.SUBCONTRACTOR_BADGE_LOGO) ||
    pendingBadges.includes(ProjectNewModel.ProjectBadgeTemplates.SUBCONTRACTOR_BADGE_TEMPLATE_FILE)
  ) {
    initialObject.subcontractorTemplate = {
      logoFileName: fileMap.subcontractorBadgeLogo ? (Object.values(fileMap.subcontractorBadgeLogo)[0] as any)?.file?.name : '',
      templateFileName: fileMap.subcontractorBadgeTemplate ? (Object.values(fileMap.subcontractorBadgeTemplate)[0] as any)?.file?.name : '',
    };
  } else if (restOfFiles.subcontractorBadgeLogo) {
    initialObject.subcontractorTemplate = {
      logoFileName: restOfFiles.subcontractorBadgeLogo,
    };
  } else if (restOfFiles.subcontractorBadgeTemplate) {
    initialObject.subcontractorTemplate = {
      templateFileName: restOfFiles.subcontractorBadgeTemplate,
    };
  }

  if (
    pendingBadges.includes(ProjectNewModel.ProjectBadgeLogos.VISITOR_BADGE_LOGO) ||
    pendingBadges.includes(ProjectNewModel.ProjectBadgeTemplates.VISITOR_BADGE_TEMPLATE_FILE)
  ) {
    initialObject.visitorTemplate = {
      logoFileName: fileMap.visitorBadgeLogo ? (Object.values(fileMap.visitorBadgeLogo)[0] as any)?.file?.name : '',
      templateFileName: fileMap.visitorBadgeTemplate ? (Object.values(fileMap.visitorBadgeTemplate)[0] as any)?.file?.name : '',
    };
  } else if (restOfFiles.visitorBadgeLogo) {
    initialObject.visitorTemplate = {
      logoFileName: restOfFiles.visitorBadgeLogo,
    };
  } else if (restOfFiles.visitorBadgeTemplate) {
    initialObject.visitorTemplate = {
      templateFileName: restOfFiles.visitorBadgeTemplate,
    };
  }

  return initialObject;
};

export const validateBadgeTagId = ({ value }) => (value || '').length < 12 && 'Please enter a valid Tag Id.';

export const sanitizeLearningGroupsDataForBulkUpdate = (data: IFcaLearningGroup[], deletedGroups: IFcaLearningGroup[]) => {
  const sanitizedData = data?.reduce(
    (updatedData, fcaLearningGroup) => {
      if (fcaLearningGroup.isModified) {
        let group = {
          ...fcaLearningGroup,
          learningGroupId: fcaLearningGroup.id,
          updateLearningGroupCourses: fcaLearningGroup?.learningGroupCourses?.map(course => {
            let updatedCourse = { ...course, learningGroupId: fcaLearningGroup.id, metadata: course.metadataResponse ? course.metadataResponse : null };
            delete updatedCourse.metadataResponse;
            return updatedCourse;
          }),
        };
        delete group.learningGroupCourses;
        updatedData.updates.push(group);
      } else if (!fcaLearningGroup.id) {
        let group = {
          ...fcaLearningGroup,
          learningGroupCourses: fcaLearningGroup?.learningGroupCourses?.map(course => {
            let updatedCourse = { ...course };
            return updatedCourse;
          }),
        };
        delete group.id;
        updatedData.inserts.push(group);
      }
      return updatedData;
    },
    {
      inserts: [],
      updates: [],
      deletes: [],
    }
  );

  sanitizedData.deletes = deletedGroups?.map(group => ({ learningGroupId: group.id, metadata: group.metadata }));

  return sanitizedData;
};

export const sanitizeLearnerWorkersDataForBulkUpdate = (workersData: Partial<WorkerModel.IWorker>[], learningGroups: FcaLearningModel.IFcaLearningGroup[]) => {
  return workersData.reduce(
    (updatedData, workerData) => {
      let allExemptedCourses = [...(workerData.exemptedCourses || [])];
      if (workerData.exemptedLearningGroups?.length > 0) {
        learningGroups?.forEach(learningGroup => {
          if (workerData.exemptedLearningGroups.includes(learningGroup.id)) {
            allExemptedCourses = [...allExemptedCourses, ...(learningGroup?.learningGroupCourses?.map(({ courseId }) => courseId) || [])];
          }
        });
      }
      updatedData.inserts.push({
        workerId: workerData.id,
        companyId: workerData.company.id,
        exemptedCourses: [...(new Set(allExemptedCourses) as any)],
      });

      return updatedData;
    },
    {
      inserts: [],
      updates: [],
      deletes: [],
    }
  );
};

export const sanitizeCertificationGroupsForOshaAndLl196 = (project: Partial<ProjectNewModel.IProject>) => {
  let sanitizedCertificationGroups = project.certificationGroups.filter(g => !g.isPresetDefault);
  const presetGroup = project.presetCertificationGroups?.[project.mustComplyWithNycLL196 ? 0 : 1];

  if ((project.mustComplyWithNycLL196 || project.mustComplyWithOSHA) && presetGroup) {
    sanitizedCertificationGroups = sanitizedCertificationGroups.map(g => ({
      ...g,
      certifications: g.certifications.filter(c => !presetGroup.certifications.find(pc => pc.id === c.id)),
    }));
    sanitizedCertificationGroups = sanitizedCertificationGroups.filter(g => g.certifications?.length > 0);

    sanitizedCertificationGroups?.push({
      ...presetGroup,
      effectiveDate:
        project.status === ResourceModel.Status.ACTIVE
          ? moment()
              .add(1, 'month')
              .format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD'),
    });
  }

  return sanitizedCertificationGroups;
};

export const isPriceNotEmpty = value => {
  return value !== '' && value !== undefined && value !== null;
};

export const sanitizeBillingRule = product => {
  let companies = [];
  if (product.isClientResponsibleForThemselves && product.isClientCoverOtherClients) {
    product.companies.forEach(company => {
      company?.coveredCompanyId
        ?.filter(item => item.id !== '-1')
        ?.forEach(item => {
          const restOfOptions = [...Object.keys(billingEngineRuleCompanyTypeMap)];
          if (restOfOptions.find(id => id === item.id || parseInt(id as string, 10) === item.id)) {
            companies.push({
              id: item.companyId,
              coveredCompanyId: null,
              coveredCompanyType: item.id,
              responsibleCompanyId: company.responsibleCompanyId,
            });
          } else {
            companies.push({
              id: item.companyId,
              coveredCompanyId: item.id,
              responsibleCompanyId: company.responsibleCompanyId,
              coveredCompanyType: BillingEngineRuleCompanyType.SelectedCompany,
            });
          }
        });
    });
  } else if (product.isClientResponsibleForThemselves) {
    companies = [];
  } else {
    companies = [{ id: product?.oldCompanies?.[0]?.id, responsibleCompanyId: product.responsibleCompanyId }];
  }
  return {
    entity: product.categoryId === '00000000-0000-0000-0000-000000000000' ? BillingEngineRuleItemType.ProductBundle : BillingEngineRuleItemType.Product,
    entityId: product.id,
    unitPrice: product.unitPrice,
    units: product.units,
    subTotal: product.subTotal,
    startDate: product.startDate,
    endDate: product.endDate,
    maxNoOfTransactions: product.maxNoOfTransactions,
    invoiceSchedule: product.invoiceSchedule,
    companies: companies,
    isClientResponsibleForThemselves: product.isClientResponsibleForThemselves,
    isClientCoverOtherClients: product.isClientCoverOtherClients,
    canShowOnPricingAgreement: product.canShowOnPricingAgreement,
    canShowInformationToCoveredClients: product.canShowInformationToCoveredClients,
    pricingAgreementVisibilities: product.pricingAgreementVisibilities,
  };
};

export const piiFieldCodes = [
  'WorkerDateOfBirth',
  'WorkerLastSSN',
  'WorkerHomeAddressLine1',
  'WorkerHomeAddressLine2',
  'WorkerHomeAddressCountry',
  'WorkerHomeAddressStateProvince',
  'WorkerHomeAddressCity',
  'WorkerHomeAddressZipPostal',
];
export const contactFieldCodes = [
  'WorkerContactEmail',
  'WorkerContactMobileNumber',
  'WorkerContactEmailOrMobilePhone',
];
